import type { BillingEntityInput } from '@pulsify/shared/dto'
import { Plus, Receipt } from 'lucide-react'
import { type FormEvent, useId, useState } from 'react'
import { toast } from 'sonner'
import {
  useBillingEntities,
  useCreateBillingEntity,
} from '~/entities/billing-entity/model/use-billing-entities'
import { useCreatePortalSession } from '~/entities/subscription/model/use-stripe'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Skeleton } from '~/shared/ui/skeleton'
import { BillingCell } from './billing-cell'

const EMPTY: BillingEntityInput = {
  legalName: '',
  street: null,
  postalCode: null,
  city: null,
  country: 'AT',
  vatId: null,
  registrationNumber: null,
  email: null,
}

/** Whose plan the page changes and who pays for it, while a profile is active. */
export function ProfileBillingCard({
  businessId,
  profileId,
  profileName,
  ownPlan,
  billingEntityId,
  selectedEntityId,
  onSelectEntity,
}: {
  businessId: string
  profileId: string
  profileName: string
  ownPlan: boolean
  billingEntityId: string | null
  selectedEntityId: string | null
  onSelectEntity: (entityId: string) => void
}) {
  const entitiesQuery = useBillingEntities(businessId)
  const entities = entitiesQuery.data ?? []
  const create = useCreateBillingEntity(businessId)
  const portal = useCreatePortalSession(businessId, profileId)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<BillingEntityInput>(EMPTY)
  const formId = useId()
  const organization = entities.find((entity) => entity.isDefault)
  const payer =
    entities.find((entity) => entity.id === billingEntityId) ?? organization ?? entities[0]
  const chosen = selectedEntityId ?? organization?.id ?? ''

  const field = (key: keyof BillingEntityInput) => ({
    id: `${formId}-${key}`,
    value: (draft[key] as string | null) ?? '',
    onChange: (event: { target: { value: string } }) =>
      setDraft((prev) => ({ ...prev, [key]: event.target.value || null })),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate(
      { ...draft, legalName: draft.legalName.trim() },
      {
        onSuccess: (entity) => {
          onSelectEntity(entity.id)
          setAdding(false)
          setDraft(EMPTY)
        },
        onError: (error) => toast.error(getErrorMessageFromError(error)),
      },
    )
  }

  return (
    <Card>
      <div className="grid divide-y md:grid-cols-2 md:divide-x md:divide-y-0">
        <BillingCell label={m.profileBillingTitle({ name: profileName })}>
          <span className="font-semibold text-2xl tracking-[-0.02em]">
            {ownPlan ? m.profileBillingOwnPlan() : m.profileBillingSharedPlan()}
          </span>
          <p className="text-muted-foreground text-sm">
            {ownPlan ? m.profileBillingOwnHint() : m.profileBillingSharedHint()}
          </p>
        </BillingCell>

        <BillingCell
          label={m.profileBillingPayer()}
          action={
            ownPlan ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => portal.mutate()}
                disabled={portal.isPending}
              >
                <Receipt />
                {m.profileBillingInvoices()}
              </Button>
            ) : adding ? null : (
              <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(true)}>
                <Plus />
                {m.profileBillingAdd()}
              </Button>
            )
          }
        >
          {ownPlan ? (
            entitiesQuery.isLoading ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <span className="truncate font-semibold text-2xl tracking-[-0.02em]">
                {payer?.legalName}
              </span>
            )
          ) : (
            <Select value={chosen} onValueChange={onSelectEntity}>
              <SelectTrigger aria-label={m.profileBillingPayer()} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {entities.map((entity) => (
                  <SelectItem key={entity.id} value={entity.id}>
                    {entity.isDefault
                      ? m.profileBillingOrganization({ name: entity.legalName })
                      : entity.legalName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </BillingCell>
      </div>

      {adding && !ownPlan ? (
        <form onSubmit={submit} className="grid gap-3 border-t p-5 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor={`${formId}-legalName`}>{m.profileBillingLegalName()}</Label>
            <Input required {...field('legalName')} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor={`${formId}-street`}>{m.profileBillingStreet()}</Label>
            <Input {...field('street')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-postalCode`}>{m.profileBillingPostalCode()}</Label>
            <Input {...field('postalCode')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-city`}>{m.profileBillingCity()}</Label>
            <Input {...field('city')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-vatId`}>{m.profileBillingVatId()}</Label>
            <Input {...field('vatId')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-email`}>{m.profileBillingEmail()}</Label>
            <Input type="email" {...field('email')} />
          </div>
          <div className="flex gap-2 pt-1 sm:col-span-2">
            <Button type="submit" disabled={create.isPending || !draft.legalName.trim()}>
              {m.profileBillingSave()}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              {m.cancel()}
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  )
}
